// DSP helpers shared by the audio layer: generated buffers (noise, vinyl crackle, rain wash and drops, reverb impulse),
// waveshaper curves, a silent WAV for iOS keep-alive, automation helpers and the voice registry.
// Buffers are generated from seeded streams, so offline renders are reproducible.
import { rng } from '../engine/core.js';

const TWO_PI = Math.PI * 2;
const caches = new WeakMap();                 // ac -> Map(key -> AudioBuffer)
function cached(ac, key, make) {
  let m = caches.get(ac);
  if (!m) caches.set(ac, (m = new Map()));
  if (!m.has(key)) m.set(key, make());
  return m.get(key);
}
const strSeed = s => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return h >>> 0; };

export const mtof = m => 440 * Math.pow(2, (m - 69) / 12);
export const ftom = f => 69 + 12 * Math.log2(f / 440);

/* ---------------- automation helpers ---------------- */
/** Freeze a param at time t. Firefox has no cancelAndHoldAtTime: there we cancel events at >= t, which is click-free
 *  because every interruptible envelope in this code is built from setTargetAtTime/setValueAtTime (no long ramps). */
export function hold(param, t) {
  if (typeof param.cancelAndHoldAtTime === 'function') param.cancelAndHoldAtTime(t);
  else param.cancelScheduledValues(t);
}
/** Glide a param to v starting at t, reaching ~95 % after dur seconds (interruptible). */
export function ramp(param, v, t, dur) {
  hold(param, t);
  param.setTargetAtTime(v, t, Math.max(dur, 0.003) / 3);
}
/** 16-point equal-power curve (0→1 when up, 1→0 otherwise) for setValueCurveAtTime. */
export function powerCurve(up, n = 16) {
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) { const a = (i / (n - 1)) * Math.PI / 2; c[i] = up ? Math.sin(a) : Math.cos(a); }
  return c;
}

/* ---------------- buffers ---------------- */
const NOISE_SEC = { white: 3.1, pink: 4.3, brown: 5.3 };
/** Looped mono noise buffer ('white' | 'pink' | 'brown'), RMS 0.2, loop seam crossfaded over 50 ms. */
export function noise(ac, type = 'white', sec = NOISE_SEC[type] || 3) {
  return cached(ac, `noise:${type}:${sec}`, () => {
    const sr = ac.sampleRate, N = Math.round(sec * sr), F = Math.round(0.05 * sr);
    const r = rng(strSeed('noise-' + type)), x = new Float32Array(N + F);
    if (type === 'pink') {
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < x.length; i++) {
        const w = r() * 2 - 1;
        b0 = 0.99886 * b0 + w * 0.0555179; b1 = 0.99332 * b1 + w * 0.0750759; b2 = 0.969 * b2 + w * 0.153852;
        b3 = 0.8665 * b3 + w * 0.3104856; b4 = 0.55 * b4 + w * 0.5329522; b5 = -0.7616 * b5 - w * 0.016898;
        x[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362; b6 = w * 0.115926;
      }
    } else if (type === 'brown') {
      // leaky integrator, then a one-pole high-pass at ~25 Hz to kill the DC drift
      const a = 1 / (1 + TWO_PI * 25 / sr);
      let last = 0, prev = 0, y = 0;
      for (let i = 0; i < x.length; i++) {
        last = (last + 0.02 * (r() * 2 - 1)) / 1.02;
        y = a * (y + last - prev); prev = last; x[i] = y;
      }
    } else for (let i = 0; i < x.length; i++) x[i] = r() * 2 - 1;
    let ss = 0;
    for (let i = F; i < x.length; i++) ss += x[i] * x[i];
    const g = 0.2 / Math.sqrt(ss / (x.length - F) || 1);
    const buf = ac.createBuffer(1, N, sr), d = buf.getChannelData(0);
    for (let i = 0; i < N; i++) d[i] = x[i] * g;
    for (let i = 0; i < F; i++) {                 // equal-power seam: tail (x[N+i]) fades into head (x[i])
      const th = (i / F) * Math.PI / 2;
      d[i] = (x[N + i] * Math.cos(th) + x[i] * Math.sin(th)) * g;
    }
    return buf;
  });
}

/** 7.2 s vinyl crackle loop (four turns at 33 1/3 rpm), mono, peak 0.9. */
export function crackle(ac) {
  return cached(ac, 'crackle', () => {
    const sr = ac.sampleRate, sec = 7.2, N = Math.round(sec * sr), r = rng(strSeed('crackle'));
    const x = new Float32Array(N);
    const add = (i, v) => { x[((i % N) + N) % N] += v; };
    for (let t = 0; ;) {                           // Poisson clicks, 9 per second
      t += -Math.log(1 - r()) / 9;
      if (t >= sec) break;
      const i0 = Math.floor(t * sr), u = r(), amp = (0.08 + 0.6 * u * u * u) * (r() < 0.5 ? -1 : 1);
      const len = 1 + Math.floor(r() * 6);
      for (let k = 0; k < len; k++) add(i0 + k, amp * Math.pow(0.55, k));
    }
    for (let t = 0; ;) {                           // pops, 0.25 per second: smoothed bumps
      t += -Math.log(1 - r()) / 0.25;
      if (t >= sec) break;
      const i0 = Math.floor(t * sr), amp = (0.5 + 0.4 * r()) * (r() < 0.5 ? -1 : 1), len = 20 + Math.floor(r() * 40);
      for (let k = 0; k < len; k++) add(i0 + k, amp * Math.sin(Math.PI * k / len));
    }
    const tick = r() * 1.8;                        // one scratch tick per 1.8 s turn, at the same place on the record
    for (let turn = 0; turn < 4; turn++) {
      const i0 = Math.floor((tick + turn * 1.8) * sr);
      for (let k = 0; k < 90; k++) add(i0 + k, 0.35 * (r() * 2 - 1) * Math.exp(-k / 25));
    }
    const a = 1 - Math.exp(-TWO_PI * 7000 / sr);   // y[n] = x[n] - 0.97 x[n-1], then one-pole LP ~7 kHz
    let prev = 0, z = 0, peak = 0;
    for (let i = 0; i < N; i++) {
      const y = x[i] - 0.97 * prev; prev = x[i];
      z += a * (y - z); x[i] = z;
      if (Math.abs(z) > peak) peak = Math.abs(z);
    }
    const buf = ac.createBuffer(1, N, sr), d = buf.getChannelData(0), g = 0.9 / (peak || 1);
    for (let i = 0; i < N; i++) d[i] = x[i] * g;
    return buf;
  });
}

/** Rain wash loop: 7.3 s, stereo, RMS 0.2. Two pink streams of their own, high-passed at 330 Hz (Q 0.7), each mostly in
 *  one ear (the two ears correlate 0.5), the loop seam crossfaded over 50 ms. */
export function wash(ac) {
  return cached(ac, 'wash', () => {
    const sr = ac.sampleRate, N = Math.round(7.3 * sr), F = Math.round(0.05 * sr);
    const w = TWO_PI * 330 / sr, al = Math.sin(w) / 1.4, c = Math.cos(w);        // the high-pass, as a biquad
    const hb = (1 + c) / 2 / (1 + al), a1 = -2 * c / (1 + al), a2 = (1 - al) / (1 + al);
    const x = [0, 1].map(ch => {
      const r = rng(strSeed('wash') + ch * 7919), y = new Float32Array(N + F);
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0, p1 = 0, p2 = 0, y1 = 0, y2 = 0;
      for (let i = 0; i < y.length; i++) {
        const wn = r() * 2 - 1;
        b0 = 0.99886 * b0 + wn * 0.0555179; b1 = 0.99332 * b1 + wn * 0.0750759; b2 = 0.969 * b2 + wn * 0.153852;
        b3 = 0.8665 * b3 + wn * 0.3104856; b4 = 0.55 * b4 + wn * 0.5329522; b5 = -0.7616 * b5 - wn * 0.016898;
        const p = b0 + b1 + b2 + b3 + b4 + b5 + b6 + wn * 0.5362, v = hb * (p - 2 * p1 + p2) - a1 * y1 - a2 * y2;
        b6 = wn * 0.115926; p2 = p1; p1 = p; y2 = y1; y1 = v; y[i] = v;
      }
      return y;
    });
    const buf = ac.createBuffer(2, N, sr), m = new Float32Array(N + F);
    for (const ch of [0, 1]) {
      let ss = 0;
      for (let i = 0; i < N + F; i++) { m[i] = x[ch][i] + 0.27 * x[1 - ch][i]; if (i >= F) ss += m[i] * m[i]; }
      const g = 0.2 / Math.sqrt(ss / N || 1), d = buf.getChannelData(ch);
      for (let i = 0; i < N; i++) d[i] = m[i] * g;
      for (let i = 0; i < F; i++) {                 // the same seam as noise()
        const th = (i / F) * Math.PI / 2;
        d[i] = (m[N + i] * Math.cos(th) + m[i] * Math.sin(th)) * g;
      }
    }
    return buf;
  });
}

/** Rain drops loop: 6.1 s, stereo, RMS 0.05. Every drop is a short hit at its own place between the speakers: a decaying
 *  sine with a noise burst on it, most of them faint and a few loud. 300 small ticks a second (1.8-7 kHz, τ 0.4-1.6 ms)
 *  and 7 close drops a second (0.5-2.2 kHz, τ 2-6 ms). A hit wraps round the loop point, so there is no seam. */
export function drops(ac) {
  return cached(ac, 'drops', () => {
    const sr = ac.sampleRate, sec = 6.1, N = Math.round(sec * sr), r = rng(strSeed('drops'));
    const L = new Float32Array(N), R = new Float32Array(N);
    const hit = (f, tau, amp, noisy) => {
      const i0 = Math.floor(r() * N), th = (0.05 + 0.9 * r()) * Math.PI / 2, gl = amp * Math.cos(th), gr = amp * Math.sin(th);
      const T = tau * sr, len = Math.min(N, Math.round(T * 6)), w = TWO_PI * Math.min(f, 0.45 * sr) / sr;
      for (let k = 0, last = 0; k < len; k++) {
        const nz = r() * 2 - 1, v = (Math.sin(w * k) + noisy * (nz + last)) * Math.exp(-k / T), i = (i0 + k) % N;
        last = nz; L[i] += gl * v; R[i] += gr * v;
      }
    };
    const flip = n => (n & 1 ? 1 : -1);             // every other hit upside down, so the loop carries no DC
    for (let n = Math.round(300 * sec); n > 0; n--) hit(1800 * Math.pow(4, r()), 0.0004 + 0.0012 * r(), flip(n) * (0.06 + 0.3 * Math.pow(r(), 3)), 0.5);
    for (let n = Math.round(7 * sec); n > 0; n--) hit(500 * Math.pow(4.4, r()), 0.002 + 0.004 * r(), flip(n) * (0.4 + 0.3 * r() * r()), 0.25);
    let ss = 0;
    for (let i = 0; i < N; i++) ss += L[i] * L[i] + R[i] * R[i];
    const buf = ac.createBuffer(2, N, sr), g = 0.05 / Math.sqrt(ss / (2 * N) || 1);
    for (const [c, x] of [[0, L], [1, R]]) { const d = buf.getChannelData(c); for (let i = 0; i < N; i++) d[i] = x[i] * g; }
    return buf;
  });
}

/** Stereo reverb impulse: pre-delay 15 ms, 60 ms fade-in, decorrelated noise decaying to RT60,
 *  one-pole damping swept 8 kHz -> 2 kHz along the tail. Use with ConvolverNode.normalize = true. */
export function impulse(ac, { sec = 2.4, rt60 = 2 } = {}) {
  return cached(ac, `ir:${sec}:${rt60}`, () => {
    const sr = ac.sampleRate, N = Math.round(sec * sr), pre = Math.round(0.015 * sr), fade = 0.06 * sr;
    const buf = ac.createBuffer(2, N, sr);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch), r = rng(strSeed('ir') + ch * 7919);
      let z = 0;
      for (let i = pre; i < N; i++) {
        const k = i - pre, t = k / sr, u = k / (N - pre);
        const fc = 8000 * Math.pow(2000 / 8000, u), a = 1 - Math.exp(-TWO_PI * fc / sr);
        z += a * ((r() * 2 - 1) - z);
        d[i] = z * Math.exp(-6.91 * t / rt60) * Math.min(1, k / fade);
      }
    }
    return buf;
  });
}

/* ---------------- waveshaper curves ---------------- */
/** Tape saturation y = (tanh(k(x+b)) - tanh(kb)) / k, 4097 points. */
export function tapeCurve(k = 2, b = 0.08) {
  const n = 4097, c = new Float32Array(n), off = Math.tanh(k * b);
  for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; c[i] = (Math.tanh(k * (x + b)) - off) / k; }
  return c;
}
/** Soft clip: linear up to 0.8, then a tanh knee towards a 0.95 ceiling. */
export function clipCurve() {
  const n = 4097, c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1, a = Math.abs(x);
    c[i] = a <= 0.8 ? x : Math.sign(x) * (0.8 + 0.15 * Math.tanh((a - 0.8) / 0.15));
  }
  return c;
}

/* ---------------- silent WAV (iOS keep-alive) ---------------- */
let silentUrl = null;
/** Blob URL of a 1 s silent 8 kHz 16-bit mono WAV, generated in JS. */
export function silentWavUrl() {
  if (silentUrl) return silentUrl;
  const sr = 8000, n = sr, bytes = new ArrayBuffer(44 + n * 2), v = new DataView(bytes);
  const str = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); str(8, 'WAVE'); str(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, sr, true);
  v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); str(36, 'data'); v.setUint32(40, n * 2, true);
  silentUrl = URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
  return silentUrl;
}

/* ---------------- voice registry ---------------- */
/** Every voice (one-shot or persistent) is registered here so it can be stopped as a group and so that all of its
 *  nodes are disconnected when it ends: onended of its first source frees it, and sweep() catches stragglers. */
export function createRegistry() {
  const live = new Set();
  let created = 0, nodes = 0;
  function free(v) {
    if (v.freed) return;
    v.freed = true; live.delete(v); nodes -= v.all.length;
    for (const s of v.src) s.onended = null;
    for (const n of v.all) { try { n.disconnect(); } catch { /* already disconnected */ } }
  }
  const stopAt = (s, t) => { try { s.stop(t); } catch { /* not started or already stopped */ } };
  return {
    /** src: started sources; all: every node of the voice (sources included); out: the gain faded by stopTag */
    add({ src = [], all = src, start = 0, end = Infinity, tag = null, out = null }) {
      const v = { src, all, start, end, tag, out, freed: false };
      created++; nodes += all.length; live.add(v);
      if (src.length) src[0].onended = () => free(v);
      return v;
    },
    free,
    /** re-stop a voice (e.g. EP voice stealing): fade `out` from t with tau, stop sources shortly after */
    stop(v, t, tau = 0.01) {
      if (v.freed) return;
      if (v.start > t + 1e-3) { for (const s of v.src) stopAt(s, v.start); v.end = v.start; return; }
      if (v.out) { hold(v.out.gain, t); v.out.gain.setTargetAtTime(0, t, tau); }
      const end = t + tau * 8;
      if (end < v.end) { for (const s of v.src) stopAt(s, end); v.end = end; }
    },
    /** stop every voice whose tag matches (value or predicate) */
    stopTag(tag, t, tau = 0.05) {
      const match = typeof tag === 'function' ? tag : x => x === tag;
      for (const v of [...live]) if (match(v.tag)) this.stop(v, t, tau);
    },
    /** free voices that should have ended long ago (onended can be skipped when a context is torn down) */
    sweep(now) { for (const v of [...live]) if (now > v.end + 2) free(v); },
    count(tag) { let n = 0; for (const v of live) if (v.tag === tag) n++; return n; },
    get live() { return live.size; },
    get created() { return created; },
    get nodes() { return nodes; },
  };
}
